import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Printer, Mail, FileText, Download, Pencil } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LOGO_URL, COMPANY, currencySymbol } from "@/lib/constants";
import FreightSummary from "@/components/FreightSummary";
import InvoiceEditDialog from "@/components/InvoiceEditDialog";

const fmtDT = (v) => {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Addr = ({ label, a, date, extraLabel, extraValue }) => (
  <div>
    <div className="mono-label">{label}</div>
    <div className="mt-1 text-sm">
      {a?.name && <div className="font-semibold">{a.name}</div>}
      <div>{a?.street}</div>
      <div>{a?.city}, {a?.state} {a?.zip}</div>
      {a?.contact_name && <div className="text-xs text-zinc-600 mt-1">Contact: {a.contact_name}{a.contact_phone && ` · ${a.contact_phone}`}</div>}
    </div>
    {date && <div className="mt-2 mono-label">Date: <span className="font-mono normal-case tracking-normal text-zinc-950">{fmtDT(date)}</span></div>}
    {extraLabel && (
      <div className="mt-1 mono-label">{extraLabel}: <span className="font-mono normal-case tracking-normal text-zinc-950">{fmtDT(extraValue) || "—"}</span></div>
    )}
  </div>
);

export default function InvoiceView() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [sending, setSending] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/invoices/${id}`);
    setInv(data);
  };

  useEffect(() => {
    (async () => {
      await load();
    })();
  }, [id]);

  const emailInvoice = async () => {
    const target = window.prompt(
      "Email invoice to (leave blank to use customer's billing email on file):",
      ""
    );
    if (target === null) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/invoices/${id}/send-email`,
        { to_email: target || undefined, attach_pod: true }
      );
      toast.success(
        `Invoice emailed to ${data.sent_to}` +
          (data.pod_attached ? ` (POD attached · ${data.attachment_count})` : "")
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (!inv) return <div className="p-10 mono-label">Loading invoice...</div>;
  const sym = currencySymbol(inv.currency);

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="no-print bg-white border-b border-zinc-200 px-6 py-3 flex items-center justify-between">
        <Link to="/admin/invoices" className="mono-label">&larr; BACK</Link>
        <div className="flex items-center gap-2">
          {inv.email_sent_at && (
            <span className="text-[11px] font-mono text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-1" data-testid="inv-sent-badge">
              SENT · {new Date(inv.email_sent_at).toLocaleString()}
            </span>
          )}
          <Button
            onClick={() => setEditOpen(true)}
            variant="outline"
            className="sharp border-zinc-300"
            data-testid="btn-edit-invoice"
          >
            <Pencil className="h-4 w-4 mr-2" /> Edit
          </Button>
          <Button
            onClick={emailInvoice}
            disabled={sending}
            variant="outline"
            className="sharp border-emerald-800 text-emerald-800 hover:bg-emerald-50"
            data-testid="btn-email-invoice"
          >
            <Mail className="h-4 w-4 mr-2" />
            {sending ? "Sending..." : inv.email_sent_at ? "Resend invoice" : "Email invoice to client"}
          </Button>
          <Button onClick={() => window.print()} className="sharp bg-zinc-950 hover:bg-zinc-800" data-testid="print-invoice">
            <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
          </Button>
        </div>
      </div>
      <div className="max-w-4xl mx-auto bg-white my-8 printable p-12 border border-zinc-950">
        <div className="flex items-start justify-between border-b-2 border-zinc-950 pb-6">
          <div className="flex items-start gap-4">
            <img src={LOGO_URL} alt="Apex" className="h-16 w-16 object-contain" />
            <div>
              <div className="text-2xl font-bold tracking-tight">{COMPANY.name}</div>
              <div className="text-xs text-zinc-600 mt-1 leading-relaxed">
                {COMPANY.address}<br />
                {COMPANY.phone} · {COMPANY.email}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="mono-label">INVOICE</div>
            <div className="font-mono text-2xl font-bold mt-1">{inv.invoice_number}</div>
            <div className="mono-label mt-2">Job {inv.job_number}</div>
            {inv.po_number && <div className="mono-label">PO {inv.po_number}</div>}
            {inv.reference && <div className="mono-label">REF {inv.reference}</div>}
            <div className={`inline-block mt-3 px-3 py-1 border font-mono text-[10px] uppercase ${
              inv.status === "paid" ? "bg-emerald-100 text-emerald-900 border-emerald-300" : "bg-amber-100 text-amber-900 border-amber-300"
            }`}>{inv.status}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mt-8">
          <div>
            <div className="mono-label">BILL TO</div>
            <div className="mt-1 text-sm">
              <div className="font-semibold">{inv.customer_company || inv.customer_name}</div>
              {inv.customer_company && <div>{inv.customer_name}</div>}
              {inv.customer_address && <div className="text-zinc-700">{inv.customer_address}</div>}
              {inv.customer_phone && (
                <div className="text-zinc-700 font-mono text-xs">
                  <span className="mono-label !text-[9px]">Ph:</span> {inv.customer_phone}
                </div>
              )}
              {inv.customer_email && (
                <div className="text-zinc-700 text-xs">
                  <span className="mono-label !text-[9px]">Email:</span> {inv.customer_email}
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="mono-label">DATES</div>
            <div className="mt-1 text-sm">
              <div>Issued: <span className="font-mono">{new Date(inv.created_at).toLocaleDateString()}</span></div>
              {inv.due_date && <div>Due: <span className="font-mono">{inv.due_date}</span></div>}
              <div className="mt-1"><span className="mono-label">Terms:</span> <span className="font-mono">{inv.payment_terms || "Net 30 days"}</span></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mt-8 pt-6 border-t border-zinc-200">
          <Addr label="PICKUP" a={inv.pickup} date={inv.pickup_date} />
          <Addr label="DELIVERY" a={inv.delivery} date={inv.delivery_date} extraLabel="Appointment" extraValue={inv.appointment_date} />
        </div>

        <FreightSummary doc={inv} />

        {Array.isArray(inv.containers) && inv.containers.length > 0 && (
          <div className="mt-8 border border-zinc-950" data-testid="invoice-containers">
            <div className="bg-zinc-950 text-white px-3 py-2 mono-label !text-zinc-100">
              CONTAINERS &middot; {inv.containers.length}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-2 px-3 mono-label">Container #</th>
                  <th className="text-left py-2 px-3 mono-label">Pickup date</th>
                  <th className="text-left py-2 px-3 mono-label">Drop date</th>
                  <th className="text-left py-2 px-3 mono-label">In-gate date</th>
                </tr>
              </thead>
              <tbody>
                {inv.containers.map((c, i) => (
                  <tr key={i} className={i % 2 ? "bg-zinc-50" : ""}>
                    <td className="py-2 px-3 font-mono font-semibold">{c.container_number || "—"}</td>
                    <td className="py-2 px-3 font-mono">{c.pickup_date || "—"}</td>
                    <td className="py-2 px-3 font-mono">{c.drop_date || "—"}</td>
                    <td className="py-2 px-3 font-mono">{c.ingate_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <table className="w-full mt-8 text-sm border border-zinc-950">
          <thead className="bg-zinc-950 text-white">
            <tr>
              <th className="text-left py-2 px-3 mono-label !text-zinc-100">Description</th>
              <th className="text-right py-2 px-3 mono-label !text-zinc-100">Qty</th>
              <th className="text-right py-2 px-3 mono-label !text-zinc-100">Rate</th>
              <th className="text-right py-2 px-3 mono-label !text-zinc-100">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(inv.lines || []).map((ln, i) => (
              <tr key={i}>
                <td className="py-3 px-3 border-t border-zinc-200">{ln.description}</td>
                <td className="py-3 px-3 border-t border-zinc-200 text-right font-mono">{ln.quantity}</td>
                <td className="py-3 px-3 border-t border-zinc-200 text-right font-mono">{sym}{Number(ln.unit_price).toFixed(2)}</td>
                <td className="py-3 px-3 border-t border-zinc-200 text-right font-mono">{sym}{(Number(ln.quantity) * Number(ln.unit_price)).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-zinc-50">
            <tr>
              <td colSpan={3} className="py-2 px-3 text-right mono-label">Subtotal</td>
              <td className="py-2 px-3 text-right font-mono">{sym}{inv.subtotal.toFixed(2)}</td>
            </tr>
            <tr>
              <td colSpan={3} className="py-2 px-3 text-right mono-label">Tax ({inv.tax_rate}%)</td>
              <td className="py-2 px-3 text-right font-mono">{sym}{inv.tax.toFixed(2)}</td>
            </tr>
            <tr className="bg-zinc-950 text-white">
              <td colSpan={3} className="py-3 px-3 text-right mono-label !text-zinc-100">TOTAL DUE ({inv.currency || "USD"})</td>
              <td className="py-3 px-3 text-right font-mono text-lg font-bold">{sym}{inv.total.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>

        {inv.notes && (
          <div className="mt-6 border border-zinc-200 p-4 bg-zinc-50">
            <div className="mono-label">NOTES</div>
            <p className="mt-2 text-sm">{inv.notes}</p>
          </div>
        )}

        {(inv.pod_files || []).length > 0 && (
          <div className="mt-8 border-t-2 border-zinc-950 pt-6">
            <div className="flex items-center justify-between">
              <div className="mono-label">PROOF OF DELIVERY &middot; ATTACHED</div>
              <span className="mono-label !text-[10px]">{inv.pod_files.length} FILE(S)</span>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              {inv.pod_files.map((f) => {
                const backendBase = API.replace(/\/api$/, "");
                const url = f.url?.startsWith("http") ? f.url : `${backendBase}${f.url}`;
                const isImg = /\.(png|jpe?g|webp|heic|gif)$/i.test(f.original_name || "");
                return (
                  <div key={f.filename} className="border border-zinc-200 bg-zinc-50" data-testid={`inv-pod-${f.filename}`}>
                    <div className="aspect-video bg-white border-b border-zinc-200 flex items-center justify-center overflow-hidden">
                      {isImg ? (
                        <img src={url} alt={f.original_name} className="w-full h-full object-cover" />
                      ) : (
                        <FileText className="h-10 w-10 text-zinc-400" />
                      )}
                    </div>
                    <div className="p-2 text-xs">
                      <div className="font-mono truncate" title={f.original_name}>{f.original_name}</div>
                      <a href={url} target="_blank" rel="noreferrer"
                         className="mt-1 text-[#0056B3] underline inline-flex items-center no-print">
                        <Download className="h-3 w-3 mr-1" /> Open
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-10 pt-6 border-t-2 border-zinc-950">
          <div className="mono-label">PAYMENT TERMS</div>
          <p className="mt-2 text-sm font-medium">Net 30 days pay terms.</p>
          <p className="text-xs text-zinc-600 mt-1">
            Please remit payment to {COMPANY.name}, {COMPANY.address}. Reference invoice #{inv.invoice_number} on all remittances.
          </p>
        </div>
      </div>

      <InvoiceEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        invoice={inv}
        onSaved={(next) => setInv(next)}
      />
    </div>
  );
}
